import { useMemo } from 'react';
import { Order, OrderJob } from '@/redux/slices/slice_types';
import LinearPercentage from '../_common/LinearPercentage';
import BulkOrdersProgressBar from './BulkOrdersProgressBar';
import { Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import PolarisIcon from '../_common/PolarisIcon';

interface OrdersProgressBarProps {
  jobs: OrderJob[];
  order: Order;
  ignoreCancelled?: boolean;
}

// the progress bar should display:
// - complete = success
// - failed + in_error = error
// - in_progress + submitted = pending
// - cancelled + created = default
// - total_jobs is our total

// these come in on the order as job_stats: {}
const OrdersProgressBar = ({ order, ignoreCancelled }: OrdersProgressBarProps) => {
  if (!order) return null;

  // if it's a bulk order that is still processing, load that component
  if (order.order_type === 'bulk' && order.status === 'bulk_processing') {
    return <BulkOrdersProgressBar order={order} />;
  }

  const values = useMemo(() => {
    const result = {
      total: 0,
      complete: 0,
      pending: 0,
      error: 0,
      cancelled: 0,
      bulk_errors: 0
    };

    // if this exists, we have the newer job_stats and we can use them
    if (order.job_stats && Object.keys(order.job_stats).length) {
      const { total_jobs = 0, complete = 0, in_error = 0, failed = 0, in_progress = 0, submitting = 0, cancelled = 0, created = 0, bulk_errors = 0 } = order.job_stats;
      result.complete = complete;
      result.pending = in_progress + submitting;
      result.error = failed + in_error;
      result.bulk_errors = bulk_errors;
      result.cancelled = ignoreCancelled ? created : cancelled + created;
      result.total = total_jobs;
    }

    // fallback for older orders that have metadata.job_stats
    else if (order.metadata?.job_stats && Object.keys(order.metadata?.job_stats).length) {
      const { total = 0, completed = 0, in_progress = 0, failed = 0, cancelled = 0 } = order.metadata.job_stats;
      result.complete = completed;
      result.pending = in_progress;
      result.error = failed;
      result.cancelled = ignoreCancelled ? 0 : cancelled;
      result.bulk_errors = order.bulk_errors?.length;
      result.total = ignoreCancelled ? total - cancelled : total;
    }

    // these are SUPER old and we rely on the jobs array, if it exists, for counts
    else if (order.jobs?.length) {
      result.complete = order.jobs.filter((job: OrderJob) => job.status.includes('complete'))?.length;
      result.error = order.jobs.filter((job: OrderJob) => job.status.includes('error') || job.status.includes('fail'))?.length;
      result.pending = result.total - result.complete - result.cancelled - result.error;
      const cancelledCount = order.jobs.filter((job: OrderJob) => job.status.includes('cancelled'))?.length;
      result.cancelled = ignoreCancelled ? 0 : cancelledCount;
      result.total = ignoreCancelled ? result.total - cancelledCount : result.total;
    }

    return result;
  }, [order]);

  return (
    <ProgressBarWrapper>
      <LinearPercentage
        total={values.total}
        complete={values.complete}
        pending={values.pending}
        error={values.error}
        cancelled={ignoreCancelled ? 0 : values.cancelled}
        tooltipText={`${values.complete} of ${values.total.toLocaleString()} job${values.total !== 1 ? 's' : ''} complete`}
      />
      {values.bulk_errors > 0 && (
        <Tooltip title={`${values.bulk_errors} error${values.bulk_errors !== 1 ? 's' : ''}`} arrow>
          <StyledPolarisIcon icon="error_filled" height={20} />
        </Tooltip>
      )}
    </ProgressBarWrapper>
  );
};

export default OrdersProgressBar;

const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
`;

const StyledPolarisIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.error.main};
  margin-left: ${({ theme }) => theme.spacing(1)};
`;
